
const scenarioPlannerSolverTypes = ['GENETIC', 'RAPID'] as const

/** Valid Scenario Planner solver algorithms */
export type ScenarioPlannerAlgorithm = typeof scenarioPlannerSolverTypes[number];

/** Validates that the value is a known ScenarioPlannerAlgorithm */
export function isAScenarioPlannerAlgorithm(val: unknown): val is ScenarioPlannerAlgorithm {
    return typeof(val) === 'string' && scenarioPlannerSolverTypes.includes(val as ScenarioPlannerAlgorithm)
}

export interface IScenarioPlannerServiceOptions {
    /** The algorithm that will be used to solve the scenario planner session */
    solverType?: ScenarioPlannerAlgorithm
}

const scenarioPlannerRequestTypeVals = ['Calculate', 'GetStatus', 'Cancel'] as const;
export interface IScenarioPlannerServiceRequest {
    RequestType: typeof scenarioPlannerRequestTypeVals[number]
    /** Use this object to specify the solver type if this is a calc request. It will be ignored otherwise */
    RequestOptions?: IScenarioPlannerServiceOptions
    wbScnPlannerId: number
    /** If true, the service call will not return until the calculation is complete (or errors out) */
    waitForResults?: boolean
}

export enum ScenarioPlannerCalcStatus {
    NotLoaded = "NotLoaded",
    ValidatingScopeAndLoadingData = "ValidatingScopeAndLoadingData",
    LoadedAndReady = "LoadedAndReady",
    ExecutingRapidCalc = "ExecutingRapidCalc",
    ExecutingGeneticAlgorithmCalc = "ExecutingGeneticAlgorithmCalc",
    WritingMetrics = "WritingMetrics",
    DoneWithSuccess = "DoneWithSuccess",
    DoneWithError = "DoneWithError",
    Unknown = "Unknown"
}
export interface IScenarioCalcStatus {
    Status: ScenarioPlannerCalcStatus,
    wbScenarioPlannerCalcId: number
}

/** Expected response to be sent back from posting to the backend */
export type IScenarioPlannerServiceResponse = IScenarioPlannerServiceRequest & {
    bSuccess: boolean
    sWarnings: string[]
    sErrors: string[]
    Status: ScenarioPlannerCalcStatus
    /** User-friendly description of what the engine is doing */
    currentStep: string
    stage?: string
    wbScenarioPlannerCalcId: number
}

export interface IScenarioCalcStatusResponse extends IScenarioPlannerServiceResponse {

}

export interface IScenarioPlannerService {
    // Scenario Calc from DB
    Calculate(wbScnPlannerId: number, opts?: IScenarioPlannerServiceOptions): Promise<IScenarioCalcStatusResponse>
    CancelCalculate(wbScnPlannerId: number, opts?: IScenarioPlannerServiceOptions): Promise<IScenarioCalcStatusResponse>
    GetCalcStatus(wbScnPlannerId: number, opts?: IScenarioPlannerServiceOptions): Promise<IScenarioCalcStatusResponse>
}

export function isAnIScenarioPlannerServiceRequest(obj: unknown): obj is IScenarioPlannerServiceRequest {
    if (typeof(obj) !== 'object' || obj === null) {
        return false
    }

    const typedObj = obj as IScenarioPlannerServiceRequest

    return (
        typeof(typedObj.RequestType) === 'string' &&
        typeof(typedObj.wbScnPlannerId) === 'number' &&
        (typedObj.RequestOptions === undefined || typeof(typedObj.RequestOptions) === 'object') &&
        (typedObj.waitForResults === undefined || typeof(typedObj.waitForResults) === 'boolean')
    )
}

export function isAnIScenarioPlannerServiceResponse(obj: unknown): obj is IScenarioPlannerServiceResponse {
    if (typeof(obj) !== 'object' || obj === null) {
        return false
    }

    const typedObj = obj as IScenarioPlannerServiceResponse

    return (
        isAnIScenarioPlannerServiceRequest(typedObj) &&
        typeof(typedObj.bSuccess) === 'boolean' &&
        Array.isArray(typedObj.sWarnings) &&
        Array.isArray(typedObj.sErrors) &&
        typeof(typedObj.Status) === 'string' &&
        typeof(typedObj.currentStep) === 'string' &&
        typeof(typedObj.wbScenarioPlannerCalcId) === 'number'
    )
}
